import { useState } from "react";
import { Menu, Search, X, ArrowRight, Compass } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useUI } from "@/context/UIContext";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { NotificationBell } from "@/components/NotificationBell";
import { MEDIA } from "@/lib/media";

const MENU = {
  Hunt: ["Find a Hunt", "Points Planner", "Outfitters", "Guides", "Private Ranches", "My Hunts", "HomeRange AI", "Gear Builder"],
  Gear: ["FIELD Product Intel", "Marketplace", "Best Value", "Trending", "Price Drops", "Optics", "Packs & Boots", "Firearms Info"],
  Field: ["Articles", "Field Guides", "Species Guides", "Stories", "Journals", "Films & Vlogs", "Wild Table"],
  "Licenses & Regs": ["All 50 States", "Hunting Licenses", "Fishing Licenses", "Tags & Draws", "Season Dates", "Hunter Education"],
  Community: ["Feed", "Hunter Profiles", "Public Journals", "Creators", "Following", "Saved Stories"],
  Business: ["Brands", "Outfitters", "Become a Partner", "List a Hunt", "Advertising", "Subscriptions"],
};

const LINK_TARGET = {
  "My Hunts": "/account", "HomeRange AI": "/ai", "Gear Builder": "/gear", "Find a Hunt": "/outfitters",
  "Points Planner": "/planner",
  Outfitters: "/outfitters", Guides: "/outfitters", "Private Ranches": "/outfitters", Marketplace: "/marketplace",
  "Best Value": "/marketplace", Trending: "/marketplace", "Price Drops": "/marketplace", Optics: "/marketplace?category=Optics",
  "FIELD Product Intel": "/marketplace",
  "Packs & Boots": "/marketplace?category=Packs", "Firearms Info": "/firearms", "All 50 States": "/explore",
  "Hunting Licenses": "/explore", "Fishing Licenses": "/explore", "Tags & Draws": "/explore",
  "Season Dates": "/explore", "Hunter Education": "/explore", Articles: "/recipes", "Field Guides": "/recipes",
  "Species Guides": "/recipes", Stories: "/field-notes", Journals: "/field-notes", "Films & Vlogs": "/recipes",
  "Wild Table": "/recipes", "Feed": "/archive", "Hunter Profiles": "/account", "Public Journals": "/field-notes",
  Creators: "/field-notes", Following: "/account", "Saved Stories": "/field-notes", Brands: "/marketplace",
  "List a Hunt": "/business", Advertising: "/advertise", Subscriptions: "/business",
  "Become a Partner": "/partner/onboarding",
};

// Fallback groups (used until the DB-driven nav loads / if it's empty).
const DEFAULT_GROUPS = Object.entries(MENU).map(([label, items]) => ({
  label,
  items: items.map((it) => ({ label: it, href: LINK_TARGET[it] || "#top" })),
}));

// Primary horizontal nav shown on desktop.
const PRIMARY_NAV = [
  { label: "HUNTS", href: "/outfitters" },
  { label: "GEAR", href: "/gear" },
  { label: "FIELD NOTES", href: "/field-notes" },
  { label: "FROM FIELD TO TABLE", href: "/recipes" },
  { label: "ARTICLES", href: "/field-notes" },
  { label: "PARTNERS", href: "/partners" },
  { label: "COMMUNITY", href: "/archive" },
];

function AnnouncementStrip() {
  const { openAuth } = useUI();
  const { isAuthed } = useAuth();
  const [closed, setClosed] = useState(() => {
    try { return localStorage.getItem("hr_announcement_dismissed_v1") === "1"; } catch { return false; }
  });
  const dismiss = () => {
    setClosed(true);
    try { localStorage.setItem("hr_announcement_dismissed_v1", "1"); } catch { /* ignore */ }
  };
  if (closed) return null;
  return (
    <div className="bg-charcoal-light text-bone/75 text-[12px] sm:text-[13px] px-4 py-2.5 relative" data-testid="announcement-strip">
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-x-2 gap-y-1 flex-wrap pr-8 text-center">
        <strong className="text-bone font-semibold">HomeRange is open to everyone.</strong>
        <span className="hidden sm:inline">Browse gear, hunts, films &amp; official regulations — no account needed.</span>
        {!isAuthed && (
          <button onClick={() => openAuth("register")} className="text-blaze font-semibold hover:text-blaze-light transition-colors" data-testid="announcement-create-account">Create account</button>
        )}
      </div>
      <button onClick={dismiss} className="absolute right-3 top-1/2 -translate-y-1/2 text-bone/50 hover:text-bone transition-colors" aria-label="Dismiss announcement" data-testid="announcement-close"><X size={16} /></button>
    </div>
  );
}

export function SiteHeader() {
  const { openAI, setProfileOpen, openAuth, setBuildHuntOpen, menuOpen, setMenuOpen } = useUI();
  const { isAuthed, user } = useAuth();
  const { nav } = useSiteContent();
  const rawGroups = Array.isArray(nav) && nav.length ? nav : DEFAULT_GROUPS;
  // Always surface the "Become a Partner" CTA in the Business group, even when nav is DB-driven.
  const groups = (() => {
    const partnerLink = { label: "Become a Partner", href: "/partner/onboarding" };
    let hasBusiness = false;
    const merged = rawGroups.map((g) => {
      if ((g.label || "").toLowerCase() !== "business") return g;
      hasBusiness = true;
      const items = g.items || [];
      const exists = items.some((it) => (it.href || "") === partnerLink.href || (it.label || "").toLowerCase() === "become a partner");
      return exists ? g : { ...g, items: [...items, partnerLink] };
    });
    return hasBusiness ? merged : [...merged, { label: "Business", items: [partnerLink] }];
  })();
  const { pathname } = useLocation();

  const initials = isAuthed ? (user.display_name || "H").slice(0, 2).toUpperCase() : "HR";
  const isActive = (href) => href === "/recipes" ? pathname.startsWith("/recipes") : pathname === href;

  return (
    <>
      <AnnouncementStrip />

      <header className="sticky top-0 z-40 bg-charcoal/95 backdrop-blur-xl border-b border-white/10" data-testid="site-header">
        <div className="max-w-[1500px] mx-auto px-3 sm:px-8 h-[72px] flex items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 min-w-0">
            <button onClick={() => setMenuOpen(true)} className="w-10 h-10 sm:w-11 sm:h-11 grid place-items-center -ml-1.5 sm:-ml-2 text-bone/90 hover:text-blaze transition-colors shrink-0" aria-label="Open menu" data-testid="menu-open-btn">
              <Menu size={22} />
            </button>
            <a href="/" className="min-w-0" data-testid="nav-logo"><Logo dark /></a>
          </div>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" data-testid="primary-nav">
            {isAuthed && (
              <a href="/my-homerange"
                className={`relative text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors py-1 ${pathname.startsWith("/my-homerange") ? "text-blaze" : "text-bone/75 hover:text-bone"}`}
                data-testid="primary-nav-my-homerange">
                MY HOMERANGE
                {pathname.startsWith("/my-homerange") && <span className="absolute left-0 -bottom-0.5 h-0.5 w-full bg-blaze rounded-full" />}
              </a>
            )}
            {PRIMARY_NAV.map((item) => (
              <a key={item.label} href={item.href}
                className={`relative text-[12px] font-semibold uppercase tracking-[0.12em] transition-colors py-1 ${isActive(item.href) ? "text-blaze" : "text-bone/75 hover:text-bone"}`}
                data-testid={`primary-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {item.label}
                {isActive(item.href) && <span className="absolute left-0 -bottom-0.5 h-0.5 w-full bg-blaze rounded-full" />}
              </a>
            ))}
          </nav>

          <nav className="flex items-center gap-1 sm:gap-3 shrink-0">
            <a href="/search" className="grid place-items-center w-9 h-9 sm:w-10 sm:h-10 text-bone/90 hover:text-blaze transition-colors" aria-label="Search" data-testid="search-btn"><Search size={19} /></a>
            {isAuthed && <NotificationBell />}
            <a href="/partner/onboarding" className="hidden xl:inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.12em] text-bone/75 hover:text-blaze transition-colors border border-white/15 hover:border-blaze rounded-full px-4 py-2.5" data-testid="header-become-partner">
              Become a Partner
            </a>
            <button onClick={() => (isAuthed ? setBuildHuntOpen(true) : openAuth("register"))} className="hidden sm:inline-flex items-center gap-1.5 bg-blaze text-white px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="header-build-hunt">
              Build My Hunt
            </button>
            <button
              onClick={() => (isAuthed ? setProfileOpen(true) : openAuth("login"))}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-earth text-bone font-bold text-xs grid place-items-center hover:bg-earth-dark transition-colors ring-1 ring-white/10 shrink-0"
              aria-label="My HomeRange"
              data-testid="profile-btn"
            >
              {initials}
            </button>
          </nav>
        </div>
      </header>

      {/* Mega menu (dark) */}
      <AnimatePresence>
        {menuOpen && (
          <motion.aside
            initial={{ y: "-100%" }} animate={{ y: 0 }} exit={{ y: "-100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[60] bg-charcoal text-bone overflow-auto hr-scroll grain"
            data-testid="mega-menu"
          >
            <div className="lg:grid lg:grid-cols-[1fr_360px] min-h-full relative z-10">
              <div className="px-6 sm:px-10 py-8">
                <div className="flex items-start justify-between border-b border-white/10 pb-6">
                  <div>
                    <span className="hr-overline text-blaze">Explore HomeRange</span>
                    <h2 className="font-serif text-4xl sm:text-5xl text-bone mt-2 leading-none">Everything for the hunt.</h2>
                  </div>
                  <button onClick={() => setMenuOpen(false)} className="text-bone/70 hover:text-blaze transition-colors" aria-label="Close menu" data-testid="menu-close-btn"><X size={34} /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8 py-8">
                  {groups.map((group) => (
                    <div key={group.label}>
                      <h3 className="font-serif text-2xl text-bone mb-3">{group.label}</h3>
                      <div className="flex flex-col gap-2">
                        {(group.items || []).map((item) => (
                          <a key={item.label} href={item.href || "#top"} onClick={() => setMenuOpen(false)} className="text-[15px] text-bone/60 hover:text-blaze transition-colors w-fit" data-testid={`menu-link-${(item.label || "").toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            {item.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-charcoal-light border border-white/10 text-bone p-6 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <span className="hr-overline text-blaze">HomeRange AI</span>
                    <h3 className="font-serif text-2xl sm:text-3xl mt-1">Tell us the hunt. We&apos;ll build the rest.</h3>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
                    <button onClick={() => { setMenuOpen(false); (isAuthed ? setBuildHuntOpen(true) : openAuth("register")); }} className="inline-flex items-center justify-center gap-2 bg-blaze text-white px-5 py-3 rounded-full font-semibold hover:bg-blaze-light transition-colors" data-testid="menu-build-hunt">Build My Hunt <Compass size={16} /></button>
                    <button onClick={() => { setMenuOpen(false); openAI(); }} className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/20 text-bone px-5 py-3 rounded-full font-semibold hover:border-blaze hover:text-blaze transition-colors" data-testid="menu-ask-ai">Ask HomeRange AI <ArrowRight size={16} /></button>
                  </div>
                </div>
              </div>
              <aside className="hidden lg:flex relative flex-col justify-end overflow-hidden" data-testid="menu-scenic">
                <img src={MEDIA.homeHero} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/70 to-charcoal/20" />
                <div className="relative z-10 p-8 text-bone">
                  <img src="/brand/badge.png" alt="HomeRange heritage badge" className="w-28 h-28 object-contain mb-5 drop-shadow-xl" loading="lazy" />
                  <h3 className="font-serif text-3xl leading-tight">Where hunters belong.</h3>
                  <p className="hr-overline text-blaze mt-2">Est. 2026 · North American Hunting</p>
                </div>
              </aside>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
